import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOfferVendorAccount } from '~/hooks/apis/price-parity/useOfferVendorAccount';
import { usePropertyParity } from '~/hooks/apis/price-parity/usePropertyParity';
import { useResolveOfferId } from '~/hooks/apis/price-parity/useResolveOfferId';
import { useGetPropertyPromotionalDiscounts } from '~/hooks/apis/reservation/useGetPropertyPromotionalDiscounts';
import { RatePlan, useGetPropertyRatePlans } from '~/hooks/apis/reservation/usePropertyRatePlans';

import PriceParrotService from '~/services/PriceParrotService';
import { formatDateISO } from '~/services/TimeService';

import { PriceSummaryJob, PriceSummaryJobStatus } from '~/types/services/priceParrot';

import { parsePropertyNameFromTAURL } from '~/utils/priceParrot';

import { analyzeParityCsv } from './analyzeParityCsv';
import { selectCheapestLPCRatePlan } from './selectCheapestLPCRatePlan';
import { selectLpcPromotionalDiscounts } from './selectLpcPromotionalDiscounts';
import { ParityAnalysis, RevenueHooks } from './types';
import { isParityHealthy } from './utils';

const NIGHT_COUNTS = [1, 2, 3, 4, 5] as const;
const DEFAULT_NIGHTS = 3;
export const MAX_DAYS_AHEAD = 364;
const POLL_INTERVAL_MS = 5000;
const JOB_FETCH_LIMIT = 100;

export type JobStatus = 'idle' | 'submitting' | 'scraping' | 'analyzing' | 'done' | 'error';

export interface UseParityToolOrchestrationResult {
  status: JobStatus;
  progress: number;
  progressText: string;
  error: string | null;
  clearError: () => void;
  analyses: Map<number, ParityAnalysis>;
  selectedNights: number;
  setSelectedNights: (n: number) => void;
  currentAnalysis: ParityAnalysis | null;
  availableNights: Array<number>;
  isHealthy: boolean;
  isWorking: boolean;
  revenueHooks: RevenueHooks | null;
  ratePlans: Array<RatePlan>;
  ratePlansLoading: boolean;
  ratePlansError: unknown;
  lpcPromotionalDiscounts: Array<App.RatePlanPromotion>;
  promotionsLoading: boolean;
  promotionsError: unknown;
  cheapestPlan: RatePlan | undefined;
  emailExpanded: boolean;
  setEmailExpanded: (expanded: boolean) => void;
  submitFreshScrape: (data: {
    locationUrl: string;
    daysAhead: number;
    currencyCode: string;
    countryCode: string;
    propertyName: string;
  }) => Promise<void>;
  loadLatestReport: (locationUrl: string, currencyCode: string, propertyName: string) => Promise<void>;
  loadFromJob: (job: PriceSummaryJob) => Promise<void>;
  cleanup: () => void;
}

export function useParityToolOrchestration(): UseParityToolOrchestrationResult {
  const [status, setStatus] = useState<JobStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<Map<number, ParityAnalysis>>(new Map());
  const [selectedNights, setSelectedNights] = useState<number>(DEFAULT_NIGHTS);
  const [emailExpanded, setEmailExpanded] = useState(false);

  // Property name drives the TanStack Query chain: name → offerId → parity + vendor account → rate plans
  const [propertyNameForLookup, setPropertyNameForLookup] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: offerId = null } = useResolveOfferId(propertyNameForLookup);
  const { data: revenueHooks = null } = usePropertyParity(offerId);
  const { data: vendorAccountId } = useOfferVendorAccount(offerId);
  const {
    data: fetchedRatePlans = [],
    isLoading: ratePlansLoading,
    error: ratePlansError,
  } = useGetPropertyRatePlans(vendorAccountId);

  const {
    data: allPromotionalDiscounts = [],
    isLoading: promotionsLoading,
    error: promotionsError,
  } = useGetPropertyPromotionalDiscounts(vendorAccountId);

  const lpcPromotionalDiscounts = useMemo(
    () => selectLpcPromotionalDiscounts(fetchedRatePlans, allPromotionalDiscounts),
    [fetchedRatePlans, allPromotionalDiscounts],
  );

  const ratePlans = fetchedRatePlans;
  const cheapestPlan = useMemo(() => selectCheapestLPCRatePlan(ratePlans), [ratePlans]);

  const currentAnalysis = analyses.get(selectedNights) ?? null;
  const availableNights = Array.from(analyses.keys()).sort((a, b) => a - b);
  const isHealthy = currentAnalysis ? isParityHealthy(currentAnalysis) : false;
  const isWorking = ['submitting', 'scraping', 'analyzing'].includes(status);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const clearError = useCallback(() => setError(null), []);

  const resetState = useCallback(() => {
    cleanup();
    setError(null);
    setAnalyses(new Map());
    setPropertyNameForLookup(null);
  }, [cleanup]);

  const analyzeAndShow = useCallback(async (jobId: string, propName: string, currency: string) => {
    const response = await PriceParrotService.downloadPriceSummaryJob(jobId);
    const csvText = await response.text();
    return analyzeParityCsv(csvText, propName, currency);
  }, []);

  const finalizeResults = useCallback((results: Map<number, ParityAnalysis>, propName: string) => {
    setAnalyses(results);
    const bestDefault = results.has(DEFAULT_NIGHTS) ? DEFAULT_NIGHTS : results.keys().next().value ?? DEFAULT_NIGHTS;
    setSelectedNights(bestDefault);
    setStatus('done');
    setProgress(100);
    setPropertyNameForLookup(propName);
  }, []);

  const loadLatestReport = useCallback(
    async (locationUrl: string, currencyCode: string, propertyName: string) => {
      resetState();
      setStatus('analyzing');
      setProgress(50);
      setProgressText('Looking for existing reports...');

      const { data: allJobs } = await PriceParrotService.getPriceSummaryJobs({ limit: JOB_FETCH_LIMIT });
      const completedForProperty = allJobs.filter(
        (j) => j.locationUrl === locationUrl && j.status === PriceSummaryJobStatus.COMPLETED,
      );

      if (completedForProperty.length === 0) {
        setStatus('error');
        setError('No completed report found for this property. Try Fresh Scrape.');
        return;
      }

      const results = new Map<number, ParityAnalysis>();

      for (const job of completedForProperty) {
        const nights = job.numberOfNights ?? DEFAULT_NIGHTS;
        if (!results.has(nights)) {
          const result = await analyzeAndShow(job.jobId, propertyName, currencyCode);
          results.set(nights, result);
        }
      }

      finalizeResults(results, propertyName);
    },
    [resetState, analyzeAndShow, finalizeResults],
  );

  const loadFromJob = useCallback(
    async (job: PriceSummaryJob) => {
      const propertyName = parsePropertyNameFromTAURL(job.locationUrl) ?? job.locationUrl;
      try {
        await loadLatestReport(job.locationUrl, job.currencyCode, propertyName);
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadLatestReport],
  );

  const submitFreshScrape = useCallback(
    async (data: {
      locationUrl: string;
      daysAhead: number;
      currencyCode: string;
      countryCode: string;
      propertyName: string;
    }) => {
      try {
        resetState();
        setStatus('submitting');
        setProgress(5);
        setProgressText(`Submitting ${NIGHT_COUNTS.length} scrape jobs (1-${NIGHT_COUNTS.length} nights)...`);

        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + Math.min(data.daysAhead, MAX_DAYS_AHEAD));

        const jobIds = new Map<number, string>();

        const submissions = NIGHT_COUNTS.map(async (nights) => {
          const {
            result: { job_id: jobId },
          } = await PriceParrotService.postPriceSummaryJob({
            location_url: data.locationUrl,
            start_date: formatDateISO(startDate),
            end_date: formatDateISO(endDate),
            number_of_nights: nights,
            currency_code: data.currencyCode,
            country_code: data.countryCode,
          });
          jobIds.set(nights, jobId);
        });
        await Promise.all(submissions);

        const propName = data.propertyName;

        setStatus('scraping');
        setProgress(10);
        setProgressText('Scraping prices for 1-5 nights...');

        const completedResults = new Map<number, ParityAnalysis>();
        const failedJobs = new Set<number>();

        const poll = async () => {
          try {
            const { data: allJobs } = await PriceParrotService.getPriceSummaryJobs({ limit: JOB_FETCH_LIMIT });

            for (const [nights, jobId] of jobIds.entries()) {
              if (completedResults.has(nights) || failedJobs.has(nights)) {
                continue;
              }

              const job = allJobs.find((j) => j.jobId === jobId);
              if (!job) {
                continue;
              }

              if (job.status === PriceSummaryJobStatus.COMPLETED) {
                const result = await analyzeAndShow(jobId, propName, data.currencyCode);
                completedResults.set(nights, result);
              } else if (
                job.status === PriceSummaryJobStatus.FAILED ||
                job.status === PriceSummaryJobStatus.CANCELLED
              ) {
                failedJobs.add(nights);
              }
            }

            const totalDone = completedResults.size + failedJobs.size;
            const pct = Math.round((totalDone / NIGHT_COUNTS.length) * 80) + 10;
            setProgress(pct);
            setProgressText(
              `Scraping prices... ${completedResults.size}/${NIGHT_COUNTS.length} complete${
                failedJobs.size > 0 ? `, ${failedJobs.size} failed` : ''
              }`,
            );

            if (totalDone === NIGHT_COUNTS.length) {
              if (completedResults.size === 0) {
                setStatus('error');
                setError('All scrape jobs failed');
                return;
              }

              finalizeResults(new Map(completedResults), propName);
            } else {
              pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            }
          } catch (e) {
            setStatus('error');
            setError(e instanceof Error ? e.message : String(e));
          }
        };
        pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [resetState, analyzeAndShow, finalizeResults],
  );

  return {
    status,
    progress,
    progressText,
    error,
    clearError,
    analyses,
    selectedNights,
    setSelectedNights,
    currentAnalysis,
    availableNights,
    isHealthy,
    isWorking,
    revenueHooks,
    ratePlans,
    ratePlansLoading,
    ratePlansError,
    lpcPromotionalDiscounts,
    promotionsLoading,
    promotionsError,
    cheapestPlan,
    emailExpanded,
    setEmailExpanded,
    submitFreshScrape,
    loadLatestReport,
    loadFromJob,
    cleanup,
  };
}
